import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthFacebookButton } from 'components/Auth';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import useGoogleTagManagerTrack from 'hooks/useGoogleTagManagerTrack';
import { authenticateUser } from 'data/api/account';
import { getCurrentUser } from 'data/api';
import { ResponseCode } from 'data/api/response-codes';
import { AuthenticateGrantType, AuthenticateProvider } from 'constants/auth';
import { GoogleTagManagerEvent } from 'constants/google';
import { CODE_LOGIN, LOGIN_WITHOUT_EMAIL_URL } from 'constants/routes';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { authenticateFailEvent, authenticateSuccessEvent, clickEvent } from 'libs/common/event-tracker/events';
import { getFingerprint } from 'libs/utils/fingerprint';
import { urlWithParams } from 'libs/utils/url';
import { navigateToPage } from 'libs/utils/window';
import { actions } from 'state/auth/slice';
import { AuthExternalRegisterView } from 'state/auth/constants';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import useSuccessUrl from '../hooks/useSuccessUrl';
import { jsx as _jsx } from "react/jsx-runtime";

const Facebook = _ref => {
  let {
    setError
  } = _ref;
  const {
    track
  } = useTracking();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const isLoginWithoutEmailInModalEnabled = useSelector(getIsFeatureSwitchEnabled('login_without_email_in_modal'));
  const {
    googleTagManagerTrack
  } = useGoogleTagManagerTrack();
  const successUrl = useSuccessUrl();
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);

  function handleClick() {
    track(clickEvent({
      target: ClickableElement.LoginWithFacebook
    }));
  }

  function trackError(error) {
    track(authenticateFailEvent({
      type: 'facebook',
      error
    }));
  }

  function handleLoginWithoutEmail(controlCode, email) {
    if (isLoginWithoutEmailInModalEnabled) {
      dispatch(actions.setExternalRegisterData({
        view: AuthExternalRegisterView.PasswordVerification,
        data: {
          idToken: controlCode,
          email
        }
      }));
      return;
    }

    navigateToPage(urlWithParams(LOGIN_WITHOUT_EMAIL_URL, {
      control_code: controlCode
    }));
  }

  async function handleSuccess(token, fields) {
    setIsLoading(true);
    const fingerprint = await getFingerprint();
    const response = await authenticateUser({
      grantType: AuthenticateGrantType.Assertion,
      provider: AuthenticateProvider.Facebook,
      assertion: token,
      fingerprint
    });

    if ('errors' in response) {
      var _response$payload;

      // TODO: handle ResponseCode.SessionFromTokenError
      if (response.code === ResponseCode.NotFound && fields.email) {
        const realName = fields.name || [fields.first_name, fields.last_name].filter(Boolean).join(' ') || undefined;
        dispatch(actions.setExternalRegisterData({
          view: AuthExternalRegisterView.FacebookRegister,
          data: {
            idToken: token,
            realName,
            email: fields.email
          }
        }));
      } else if (response.code === ResponseCode.LoginWithoutEmail && (_response$payload = response.payload) !== null && _response$payload !== void 0 && _response$payload.control_code) {
        handleLoginWithoutEmail(response.payload.control_code, fields.email);
      } else if (response.code === ResponseCode.Required2FA && response.payload) {
        navigateToPage(CODE_LOGIN({
          refUrl: successUrl,
          authProps: response.payload
        }));
      } else {
        if (fields.email) {
          trackError(response.message);
        } else {
          trackError("FB API didn't return an email");
        }

        setIsLoading(false);
        setError(response.message);
      }

      return;
    }

    const getCurrentUserResp = await getCurrentUser();
    const userId = 'errors' in getCurrentUserResp ? undefined : getCurrentUserResp.user.id;
    googleTagManagerTrack(GoogleTagManagerEvent.Login, {
      auth_type: 'facebook',
      user_email: fields.email
    });
    track(authenticateSuccessEvent({
      type: 'facebook',
      userId
    }));
    navigateToPage(successUrl);
  }

  function handleFailure() {
    trackError('User closed screen or did not provide permissions');
  }

  return /*#__PURE__*/_jsx(AuthFacebookButton, {
    text: translate('auth.select_type.actions.facebook'),
    isLoading: isLoading,
    onClick: handleClick,
    onSuccess: handleSuccess,
    onFailure: handleFailure
  });
};

export default Facebook;